import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { captureCaughtException } from "@/lib/analytics";
import { cn } from "@repo/design-system/utils";

export interface ErrorBoundaryProps {
  /** Children to render */
  children: ReactNode;
  /** Custom fallback UI (optional) */
  fallback?: ReactNode;
  /** Custom error handler (optional) */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Context identifier for error tracking */
  context?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component that catches JavaScript errors in child components.
 *
 * Features:
 * - Catches errors in render, lifecycle methods, and constructors
 * - Shows user-friendly error message
 * - Provides retry and navigation options
 * - Optional custom fallback UI
 * - Error reporting callback
 *
 * @example
 * ```tsx
 * <ErrorBoundary context="checkout">
 *   <CheckoutPage />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to console in development
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // React does not rethrow a caught render error, so `capture_exceptions`
    // never sees it - report it explicitly (KAN-248).
    captureCaughtException(error, {
      boundary: this.props.context ?? "error-boundary"
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorFallback
          error={this.state.error}
          onRetry={this.handleRetry}
          context={this.props.context}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Default error fallback UI
 */
interface ErrorFallbackProps {
  error: Error | null;
  onRetry: () => void;
  context?: string;
}

function ErrorFallback({ error, onRetry, context }: ErrorFallbackProps) {
  return (
    <div className="flex min-h-[400px] items-center justify-center p-6">
      <div
        className="w-full max-w-md rounded-xl border border-red-200 bg-white p-8 text-center shadow-sm"
        role="alert"
        aria-live="assertive"
      >
        {/* Error Icon */}
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <AlertTriangle className="h-8 w-8 text-red-600" aria-hidden="true" />
        </div>

        {/* Error Message */}
        <h2 className="mb-2 text-xl font-semibold text-gray-900">
          Something went wrong
        </h2>
        <p className="mb-4 text-sm text-gray-600">
          {context
            ? `An error occurred in ${context}. Please try again.`
            : "An unexpected error occurred. Please try again."}
        </p>

        {/* Error Details (development only) */}
        {process.env.NODE_ENV === "development" && error && (
          <div className="mb-6 rounded-lg bg-red-50 p-3 text-left">
            <p className="break-words font-mono text-xs text-red-700">
              {error.message}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Button
            onClick={onRetry}
            variant="default"
            className={cn(
              "inline-flex items-center gap-2",
              "bg-purple-600 hover:bg-purple-700"
            )}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Try Again
          </Button>
          <Button variant="outline" asChild>
            <Link to="/" className="inline-flex items-center gap-2">
              <Home className="h-4 w-4" aria-hidden="true" />
              Go Home
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ErrorBoundary;
